import React, { useState, useEffect } from "react";
import api from "../api/api"; // Axios instance with token
import Swal from "sweetalert2";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import SchoolLogo from "./messiahlogo.png";
import {
  Box, Typography, Grid, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, Button, CircularProgress, Pagination
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const PaymentRecord = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all"); // all / clear / arrears
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // PDF/Excel filters
  const [pdfGradeClass, setPdfGradeClass] = useState("");
  const [pdfSemester, setPdfSemester] = useState("");
  const [pdfStatus, setPdfStatus] = useState(""); // clear / arrears
  const [pdfInstallment, setPdfInstallment] = useState(""); // 1st, 2nd, 3rd, 4th

  // Fetch all data
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/users/semester-payment-status-all/");
      setData(res.data);
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Failed to fetch payment records", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  if (loading) return (
    <Box display="flex" justifyContent="center" mt={5}><CircularProgress /></Box>
  );

  if (!data || data.length === 0) return (
    <Box mt={5} textAlign="center">
      <Typography variant="h6">No payment records found</Typography>
    </Box>
  );

  // ------------------- Chart Data -------------------
  const totals = data.reduce(
    (acc, d) => {
      acc.clear += d.totals.clear_total;
      acc.arrears += d.totals.arrears_total;
      return acc;
    },
    { clear: 0, arrears: 0 }
  );

  const chartData = {
    labels: ["Clear Students", "Arrears Students"],
    datasets: [
      { label: "Students Count", data: [totals.clear, totals.arrears], backgroundColor: ["#4caf50", "#f44336"] }
    ]
  };

  // ------------------- Filtered Students -------------------
  const getFilteredStudents = () => {
    let students = [];
    data.forEach(d => {
      if (filterType === "clear") students.push(...d.clear_students);
      else if (filterType === "arrears") students.push(...d.arrears_students);
      else students.push(...d.clear_students, ...d.arrears_students);
    });

    if (searchTerm) {
      students = students.filter(s => s.student_name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return students.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  };

  // ------------------- PDF / Excel -------------------
  const filterPdfStudents = () => {
    if (!pdfGradeClass || !pdfSemester || !pdfStatus || !pdfInstallment) return [];

    const filteredData = data.filter(
      d => d.grade_class === pdfGradeClass && d.semester === pdfSemester
    );

    let students = [];
    filteredData.forEach(d => {
      const studentList = pdfStatus === "clear" ? d.clear_students : d.arrears_students;
      students.push(...studentList.filter(s =>
        s.payments.some(p => p.installment === pdfInstallment &&
                              (pdfStatus === "clear" ? p.is_installment_clear : p.installment_balance > 0))
      ));
    });

    return students;
  };

  const downloadPDF = () => {
    const students = filterPdfStudents();
    if (!students.length) {
      Swal.fire("Warning", "No students match the selected filters", "warning");
      return;
    }

    const element = document.getElementById("pdf-content");
    element.style.display = "block";
    html2pdf()
      .set({
        margin: [10, 10, 20, 10],
        filename: `Messiah_Payment_Report.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
      })
      .from(element)
      .save()
      .then(() => (element.style.display = "none"));
  };

  const downloadExcel = () => {
    const students = filterPdfStudents();
    if (!students.length) {
      Swal.fire("Warning", "No students match the selected filters", "warning");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      students.map(s => {
        const p = s.payments.find(p => p.installment === pdfInstallment);
        return {
      "Student Name": s.student_name,
      "Student Type": s.student_type,
      "Total Fee": s.total_fee,
      "Total Paid": s.total_paid,
      "Balance": s.balance,
      "Installment": pdfInstallment,
      "Installment Total": p.installment_total, // Added
      "Installment Paid": p.amount_paid,
      "Installment Balance": p.installment_balance,
      "Is Installment Clear": p.is_installment_clear ? "Yes" : "No"        };
      })
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
    XLSX.writeFile(workbook, `Messiah_Payment_Report.xlsx`);
  };

  // ------------------- Unique Dropdowns -------------------
  const uniqueGradeClasses = [...new Set(data.map(d => d.grade_class))];
  const uniqueSemesters = [...new Set(data.map(d => d.semester))];

  // ------------------- Render -------------------
  return (
    <Box p={2}>
      {/* Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" mb={2}>Semester Payment Summary</Typography>
        <Bar data={chartData} />
      </Paper>

      {/* Filters & Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField label="Filter Students" select fullWidth value={filterType} onChange={e => setFilterType(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="clear">Clear</MenuItem>
              <MenuItem value="arrears">Arrears</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Search Student" fullWidth value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* Students Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" mb={2}>Students List ({getFilteredStudents().length})</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student Name</TableCell>
                <TableCell>Student Type</TableCell>
                <TableCell>Total Fee</TableCell>
                <TableCell>Total Paid</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Installments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredStudents().map((s, i) => (
                <TableRow key={i}>
                  <TableCell>{s.student_name}</TableCell>
                  <TableCell>{s.student_type} student</TableCell>
                  <TableCell>{s.total_fee}</TableCell>
                  <TableCell>{s.total_paid}</TableCell>
                  <TableCell style={{ color: s.balance > 0 ? "#f44336" : "#4caf50", fontWeight: "bold" }}>{s.balance}</TableCell>
                  <TableCell>
                    {s.payments.map((p, idx) => (
                      <Box key={idx}>{p.installment}: Paid {p.amount_paid}, Balance {p.installment_balance}</Box>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={Math.ceil(getFilteredStudents().length / rowsPerPage)} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      </Paper>

      {/* PDF/Excel Filters */}
      <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField label="Grade Class" select value={pdfGradeClass} onChange={e => setPdfGradeClass(e.target.value)}>
          <MenuItem value="">Select</MenuItem>
          {uniqueGradeClasses.map((gc, i) => <MenuItem key={i} value={gc}>{gc}</MenuItem>)}
        </TextField>
        <TextField label="Semester" select value={pdfSemester} onChange={e => setPdfSemester(e.target.value)}>
          <MenuItem value="">Select</MenuItem>
          {uniqueSemesters.map((s, i) => <MenuItem key={i} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField label="Status" select value={pdfStatus} onChange={e => setPdfStatus(e.target.value)}>
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="clear">Clear</MenuItem>
          <MenuItem value="arrears">Arrears</MenuItem>
        </TextField>
        <TextField label="Installment" select value={pdfInstallment} onChange={e => setPdfInstallment(e.target.value)}>
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="1st Installment">1st Installment</MenuItem>
          <MenuItem value="2nd Installment">2nd Installment</MenuItem>
          <MenuItem value="3rd Installment">3rd Installment</MenuItem>
          <MenuItem value="4th Installment">4th Installment</MenuItem>
        </TextField>
        <Button variant="contained" color="primary" onClick={downloadPDF}>Download PDF</Button>
        <Button variant="contained" color="secondary" onClick={downloadExcel}>Download Excel</Button>
      </Paper>

      {/* Hidden PDF Content */}
      <Box id="pdf-content" sx={{ display: "none", p: 3 }}>
        <Typography variant="h5" textAlign="center" mb={2}>Messiah School Payment Report</Typography>
        {filterPdfStudents().map((s,  i) => (
          <Table key={i} sx={{ mb: 2 }}>
<TableHead>
  <TableRow sx={{ backgroundColor: 'black' }}>
    <TableCell sx={{ color: 'white' }}>Student Name</TableCell>
    <TableCell sx={{ color: 'white' }}>Type</TableCell>
    <TableCell sx={{ color: 'white' }}>Total Fee</TableCell>
    <TableCell sx={{ color: 'white' }}>Total Paid</TableCell>
    <TableCell sx={{ color: 'white' }}>Balance</TableCell>
    <TableCell sx={{ color: 'white' }}>Installment</TableCell>
    <TableCell sx={{ color: 'white' }}>Installment Total</TableCell> {/* Add this */}
    <TableCell sx={{ color: 'white' }}>Installment Paid</TableCell>
    <TableCell sx={{ color: 'white' }}>Installment Balance</TableCell>
    <TableCell sx={{ color: 'white' }}>Clear</TableCell>
  </TableRow>
</TableHead>
<TableBody>
  <TableRow sx={{
          backgroundColor: i % 2 === 0 ? '#f5f5f5' : '#ffffff' // Alternating colors
        }}>
    <TableCell>{s.student_name}</TableCell>
    <TableCell>{s.student_type}</TableCell>
    <TableCell>{s.total_fee}</TableCell>
    <TableCell>{s.total_paid}</TableCell>
    <TableCell>{s.balance}</TableCell>
    {(() => {
      const p = s.payments.find(p => p.installment === pdfInstallment);
      return (
        <>
       <TableCell sx={{backgroundColor: '#424242', color: '#ffffff', fontWeight: 600, }}>
         {p.installment}
          </TableCell>

          <TableCell>{p.installment_total}</TableCell> {/* Added */}
          <TableCell>{p.amount_paid}</TableCell>
          <TableCell>{p.installment_balance}</TableCell>
          <TableCell>{p.is_installment_clear ? "Yes" : "No"}</TableCell>
        </>
      );
    })()}
  </TableRow>
</TableBody>          </Table>
        ))}
        <Box mt={3} textAlign="center"><Typography>Polu School Management System</Typography></Box>
      </Box>
    </Box>
  );
};

export default PaymentRecord;
